import { Link } from "@tanstack/react-router";
import { Calendar, CircleCheck, EyeOff, Shield } from "lucide-react";
import type { ReactNode } from "react";

import { cn } from "../utils";
import { EventMeta } from "./EventMeta";
import { Badge } from "./ui/badge";

/**
 * One event, as a card, wherever a list of events appears.
 *
 * Two surfaces show events now and they have to look the same: the dashboard
 * ("My Events", from membership documents) and the browse page ("All events",
 * from public_events). The two read different collections with different field
 * names, so the shared thing is this card and the normalised shape below, not
 * a shared query.
 *
 * Both callers pass the description as the stored HTML. Stripping it here is
 * what stops one surface rendering markup the other strips.
 */
export interface EventSummary {
  title: string;
  startDate?: string;
  endDate?: string;
  location?: string | null;
  /** Stored HTML. Stripped to text for the two-line clamp below. */
  description?: string | null;
  image?: string | null;
}

/**
 * Where the card goes when clicked, and it must be a page that EXISTS.
 *
 * `public` is the event page, `/e/{slug}`. Its slug is not the event's stored
 * slug: `public_events/{slug}` is written only for a published event, and a
 * membership card carries the slug only once that projection is there, so the
 * stored slug of a newly created event links to "Event not found".
 *
 * `admin` is the event's administration page. An unpublished event has no
 * public page, and before this target existed its admin's card on My Events
 * opened nothing at all, which is how an admin who did not create the event
 * (and so was never taken to that page) found no way in.
 *
 * Absent or null renders the card unlinked.
 */
export type EventCardTarget =
  { page: "public"; slug: string } | { page: "admin"; eventId: string };

export function EventSummaryCard({
  event,
  overlayBadge,
  titleBadge,
  target,
  note,
  unpublished = false,
  testId = "event-card",
}: {
  event: EventSummary;
  target?: EventCardTarget | null;
  /** A short line under the details, for why the card leads nowhere. */
  note?: ReactNode;
  /**
   * The event is not published yet. The card is muted and carries the
   * "Unpublished" badge, so the state reads as text and not only as the
   * paler colour. Only the image band is muted: the text keeps the colours
   * every other card has, so its contrast does not change.
   */
  unpublished?: boolean;
  /** Sits on the image band, top right. */
  overlayBadge?: ReactNode;
  /** Sits beside the title. */
  titleBadge?: ReactNode;
  testId?: string;
}) {
  const description = plainText(event.description);

  // `h-full` down the whole chain, because a grid row stretches the grid ITEM
  // and nothing below it. The item is the Link when the card is addressable and
  // the card itself when it is not, so both have to carry it or a row of cards
  // ends up as tall as each card's own content: 390, 288 and 259 pixels side by
  // side on the browse page.
  //
  // The edge is a quiet 1px border in light and a light-blue hairline in Dusk,
  // and the lit shadow comes on hover in Dusk only, so a grid of cards stays
  // calm until one is pointed at.
  const card = (
    <div
      className="group relative flex h-full flex-col overflow-hidden rounded-lg border border-border bg-card text-card-foreground wrap-anywhere transition-shadow hover:shadow-card-hover dark:border-edge dark:border-lit-edge dark:hover:shadow-lit"
      data-testid={testId}
      data-published={unpublished ? "false" : undefined}
    >
      {event.image ? (
        <div className="relative h-36 w-full shrink-0 overflow-hidden">
          <img
            src={event.image}
            alt={`${event.title} event`}
            className={cn(
              "h-full w-full object-cover transition-transform group-hover:scale-105",
              unpublished && "opacity-60 grayscale",
            )}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-hb-dusk-deep/60 to-transparent" />
          {unpublished && <CardUnpublishedBadge />}
          {overlayBadge}
        </div>
      ) : (
        <div
          className={cn(
            "relative flex h-36 shrink-0 items-center justify-center",
            unpublished ? "bg-muted" : "bg-dusk-tile-a",
          )}
        >
          <Calendar className="h-10 w-10 text-white/40" aria-hidden="true" />
          {unpublished && <CardUnpublishedBadge />}
          {overlayBadge}
        </div>
      )}

      <div className="p-4">
        <div className="flex items-start justify-between gap-3">
          <h3 className="min-w-0 text-lg font-semibold text-foreground">
            {event.title}
          </h3>
          {titleBadge}
        </div>

        <EventMeta
          startDate={event.startDate}
          endDate={event.endDate}
          location={event.location}
          layout="stack"
          className="mt-1.5"
        />
        {description && (
          <p className="mt-2 line-clamp-2 text-sm text-muted-foreground">
            {description}
          </p>
        )}
        {note && (
          <p
            className={cn(
              "mt-2 text-xs",
              unpublished
                ? "font-medium text-foreground"
                : "text-muted-foreground",
            )}
            data-testid={`${testId}-note`}
          >
            {note}
          </p>
        )}
      </div>
    </div>
  );

  if (target?.page === "public") {
    return (
      <Link
        to="/e/$slug"
        params={{ slug: target.slug }}
        className="block h-full"
      >
        {card}
      </Link>
    );
  }

  if (target?.page === "admin") {
    return (
      <Link
        to="/admin/event/$eventId"
        params={{ eventId: target.eventId }}
        className="block h-full"
      >
        {card}
      </Link>
    );
  }

  return card;
}

export function AdminBadge() {
  return (
    <Badge className="absolute right-3 top-3 rounded-full bg-hb-dusk-deep text-white">
      <Shield className="mr-1 h-3 w-3" />
      Admin
    </Badge>
  );
}

/**
 * The one "Unpublished" badge, on the governance events list and on event
 * cards, so an unpublished event is named the same way wherever it appears.
 */
export function UnpublishedBadge({ className }: { className?: string }) {
  return (
    <Badge variant="outline" className={className}>
      <EyeOff className="mr-1 h-3 w-3" aria-hidden="true" />
      Unpublished
    </Badge>
  );
}

// Over the image band, top left, opposite the Admin badge. The band can be a
// photo, so the badge brings its own opaque background for its text.
function CardUnpublishedBadge() {
  return (
    <UnpublishedBadge className="absolute left-3 top-3 bg-background text-foreground" />
  );
}

export function RegisteredBadge() {
  return (
    <Badge variant="success">
      <CircleCheck className="mr-1 h-3 w-3" />
      Registered
    </Badge>
  );
}

function plainText(html: string | null | undefined): string {
  if (!html) return "";
  return (
    new DOMParser()
      .parseFromString(html, "text/html")
      .body.textContent?.trim() ?? ""
  );
}
